import React, { useState } from "react";
import { TransformedMetricData, LineChartData } from "./PerformanceTypes";
import {
  StyledMetricCard,
  MetricTitle,
  MetricValue,
  MetricCardHeader,
  MetricMenuButton,
} from "./PerformanceStyles";
import { PerformanceSkeleton } from "./PerformanceSkeleton";
import PercentageChangeIndicator from "./PercantageChangeIndicator";
import MetricLineChart from "./MetricLineChart";
import { Box, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useIntl } from "react-intl";
import SetGoalModal from "./SetGoalModal";
import { selectIsReportAPIAvailable } from "store/performance-report/PerformanceReportSelectors";
import { useSelector } from "react-redux";

interface MetricCardProps {
  data: TransformedMetricData;
  lineChartData?: LineChartData;
  className?: string;
  onSetGoal?: (metricId: string, goal: number) => void;
  goal?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({
  data,
  lineChartData,
  className,
  onSetGoal,
  goal,
}) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSetGoalClick = () => {
    handleMenuClose();
    setIsGoalModalOpen(true);
  };

  const handleGoalModalClose = () => {
    setIsGoalModalOpen(false);
  };

  const handleGoalSave = (goalValue: number) => {
    if (onSetGoal) {
      onSetGoal(data.id, goalValue);
    }
  };

  if (data.isLoading) {
    return <PerformanceSkeleton type="metric-card" title={data.title} />;
  }

  const isLineChartDataAvailable =
    lineChartData && lineChartData?.data && lineChartData?.data?.length > 0;

  return (
    <>
      <StyledMetricCard className={className}>
        <MetricCardHeader>
          <MetricTitle>{data.title}</MetricTitle>
          {onSetGoal && isReportAPIAvailable && (
            <MetricMenuButton
              aria-label="metric options"
              aria-controls={menuOpen ? "metric-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
              onClick={handleMenuClick}
              size="small"
            >
              <MoreVertIcon fontSize="small" />
            </MetricMenuButton>
          )}
        </MetricCardHeader>
        <MetricValue>{data.displayValue}</MetricValue>
        {data.percentageChange !== undefined && (
          <PercentageChangeIndicator
            percentageChange={data.percentageChange}
            polarity={data.polarity}
          />
        )}
        {isLineChartDataAvailable ? (
          <Box style={{ marginTop: "16px" }}>
            <MetricLineChart data={lineChartData} goal={goal} />
          </Box>
        ) : null}
      </StyledMetricCard>

      <Menu
        id="metric-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem sx={{ fontSize: "14px" }} onClick={handleSetGoalClick}>
          {intl.formatMessage({
            id: "metricCard.setGoal",
            defaultMessage: "Set Goal",
          })}
        </MenuItem>
      </Menu>

      <SetGoalModal
        open={isGoalModalOpen}
        onClose={handleGoalModalClose}
        onSave={handleGoalSave}
        currentGoal={goal}
      />
    </>
  );
};

export default MetricCard;
